import { CloseOutlined } from '@ant-design/icons';
import { Button, message, Form } from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import modal from '~components/modal';
import { CustomLengthEnum, DailyPriceTypes } from 'common/constants/price-calc-display';
import SelectList from '../select-list';
import { DailyPriceCalculationOptions } from '../index';

const getMutationCustomLengthValue = (method, customMonthLength, customYearLength) => {
  const customLengthValues = {
    [DailyPriceTypes.CUSTOM_LENGTH]: customMonthLength,
    [DailyPriceTypes.CONVERTED_ANNUAL]: customYearLength,
    [DailyPriceTypes.CONVERTED_WEEKLY]: 7,
  };

  return customLengthValues[method] ? { customLength: customLengthValues[method] } : {};
};

function DailyPriceCalcEditor(props) {
  const { handleCloseModal, form, editingPriceCalculationRule, propertyId } = props;
  const { getFieldDecorator } = form;
  const { t } = useTranslation();

  const [selectedMethod, setSelectedMethod] = useState(
    editingPriceCalculationRule?.dailyPriceType || DailyPriceTypes.CUSTOM_LENGTH,
  );

  const onSubmit = () => {
    form.validateFieldsAndScroll(async err => {
      if (err) {
        message.warn('Please check your input.');
        return;
      }
      const formData = form.getFieldsValue();

      const mutationVariables = {
        dailyPriceType: selectedMethod,

        ...getMutationCustomLengthValue(
          selectedMethod,
          formData.customMonthLength,
          formData.customYearLength,
        ),
      };

      try {
        handleCloseModal({
          priceCalculationRule: {
            ...(editingPriceCalculationRule
              ? { id: editingPriceCalculationRule?.id }
              : { propertyId }),
            ...mutationVariables,
          },
        });
      } catch (error) {
        message.error(error.toString());
      }
    });
  };

  const getCustomLengthInitValue = opt => {
    // Only when select option equal current selected option, return customLength, or the value not belong to you.
    if (opt === editingPriceCalculationRule.dailyPriceType) {
      return editingPriceCalculationRule.customLength;
    }
    return undefined;
  };

  // the decorator just for trigger form recollect.
  form.getFieldDecorator('selectedMethod', { initialValue: selectedMethod });
  return (
    <div className="daily-calc-display-editor-modal">
      <div className="modal__header">
        {t(`cms.property.listing_management.price-calc-display.daily_price_editor.title`)}
        <CloseOutlined className="modal__close-button" onClick={handleCloseModal} />
      </div>
      <div className="modal__body">
        <section className="daily-calc-display-editor-modal__choose-method">
          <h3>
            {t(
              'cms.property.listing_management.price-calc-display.final_price_editor.choose_method',
            )}
            :
          </h3>

          <SelectList.Group
            value={selectedMethod}
            onChange={method => {
              setSelectedMethod(method);
              form.setFieldsValue({ selectedMethod: method });
            }}
          >
            <SelectList.Item
              name={'CUSTOM_LENGTH'}
              label={t(DailyPriceCalculationOptions.CUSTOM_LENGTH.label)}
              des={t(DailyPriceCalculationOptions.CUSTOM_LENGTH.des)}
              icon={DailyPriceCalculationOptions.CUSTOM_LENGTH.icon}
              renderSetting={() => {
                return (
                  <>
                    <p>
                      {t(
                        'cms.property.listing_management.price-calc-display.daily_price_editor.custom_length.des',
                      )}
                    </p>

                    <Form.Item
                      label={
                        <label className="daily-calc-display-editor-modal__field-label">
                          {t(
                            'cms.property.listing_management.price-calc-display.daily_price_editor.day_in_month',
                          )}
                        </label>
                      }
                      required
                    >
                      {getFieldDecorator('customMonthLength', {
                        rules: [
                          {
                            required: true,
                            message: t('cms.listing.modal.error_message.can_not_empty'),
                          },
                          {
                            validator: (_rule, value, callback) => {
                              if (
                                ![
                                  CustomLengthEnum.TWENTY_EIGHT,
                                  CustomLengthEnum.TWENTY_NINE,
                                  CustomLengthEnum.THIRTY,
                                  CustomLengthEnum.THIRTY_POINT_THREE_THREE,
                                  CustomLengthEnum.THIRTY_ONE,
                                ].includes(value)
                              ) {
                                return callback(t('cms.properties.edit.error.blank'));
                              }

                              callback();
                            },
                          },
                        ],
                        initialValue:
                          getCustomLengthInitValue('CUSTOM_LENGTH') ||
                          CustomLengthEnum.TWENTY_EIGHT,
                      })(
                        <SelectList.ButtonList
                          options={[
                            {
                              label: CustomLengthEnum.TWENTY_EIGHT,
                              value: CustomLengthEnum.TWENTY_EIGHT,
                            },
                            {
                              label: CustomLengthEnum.TWENTY_NINE,
                              value: CustomLengthEnum.TWENTY_NINE,
                            },
                            {
                              label: CustomLengthEnum.THIRTY,
                              value: CustomLengthEnum.THIRTY,
                            },
                            {
                              label: CustomLengthEnum.THIRTY_POINT_THREE_THREE,
                              value: CustomLengthEnum.THIRTY_POINT_THREE_THREE,
                            },
                            {
                              label: CustomLengthEnum.THIRTY_ONE,
                              value: CustomLengthEnum.THIRTY_ONE,
                            },
                          ]}
                        />,
                      )}
                    </Form.Item>
                  </>
                );
              }}
            />
            <SelectList.Item
              name={'CALENDAR_MONTH_DAYS'}
              label={t(DailyPriceCalculationOptions.CALENDAR_MONTH_DAYS.label)}
              des={t(DailyPriceCalculationOptions.CALENDAR_MONTH_DAYS.des)}
              icon={DailyPriceCalculationOptions.CALENDAR_MONTH_DAYS.icon}
              renderSetting={() => {
                return (
                  <p>
                    {t(
                      'cms.property.listing_management.price-calc-display.daily_price_type.calendar_month_days.extra_des.1',
                    )}
                  </p>
                );
              }}
            />

            <SelectList.Item
              name={'CONVERTED_WEEKLY'}
              label={t(DailyPriceCalculationOptions.CONVERTED_WEEKLY.label)}
              des={t(DailyPriceCalculationOptions.CONVERTED_WEEKLY.des)}
              icon={DailyPriceCalculationOptions.CONVERTED_WEEKLY.icon}
            />

            <SelectList.Item
              name={'CONVERTED_ANNUAL'}
              label={t(DailyPriceCalculationOptions.CONVERTED_ANNUAL.label)}
              des={t(DailyPriceCalculationOptions.CONVERTED_ANNUAL.des)}
              icon={DailyPriceCalculationOptions.CONVERTED_ANNUAL.icon}
              renderSetting={() => {
                return (
                  <>
                    <Form.Item
                      label={
                        <label className="daily-calc-display-editor-modal__field-label">
                          {t(
                            'cms.property.listing_management.price-calc-display.daily_price_editor.day_in_year',
                          )}
                        </label>
                      }
                      required
                    >
                      {getFieldDecorator('customYearLength', {
                        rules: [
                          {
                            required: true,
                            message: t('cms.listing.modal.error_message.can_not_empty'),
                          },
                          {
                            validator: (_rule, value, callback) => {
                              if (![365, 366].includes(value)) {
                                return callback(t('cms.properties.edit.error.blank'));
                              }

                              callback();
                            },
                          },
                        ],
                        initialValue: getCustomLengthInitValue('CONVERTED_ANNUAL') || 365,
                      })(
                        <SelectList.ButtonList
                          options={[
                            { label: '365', value: 365 },
                            { label: '366', value: 366 },
                          ]}
                        />,
                      )}
                    </Form.Item>
                  </>
                );
              }}
            />
          </SelectList.Group>
        </section>
      </div>

      <div className="modal__footer">
        <Button size="large" onClick={handleCloseModal}>
          {t('cms.referral.promotion.modal.btn.cancel')}
        </Button>

        <Button type="primary" size="large" onClick={onSubmit}>
          {t('cms.properties.edit.btn.confirm')}
        </Button>
      </div>
    </div>
  );
}

DailyPriceCalcEditor.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
};

DailyPriceCalcEditor.defaultProps = {
  handleCloseModal: () => {},
};

export default Form.create()(
  modal(
    {
      className: 'daily-calc-display-editor-modal-container',
      useDefaultStyle: true,
    },
    true,
  )(DailyPriceCalcEditor),
);
